import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Lightbulb, Loader2, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDateTime } from '@/lib/dates'
import { formatTicketRef } from '@/lib/ticketRef'
import { useTicket } from '@/queries/tickets'
import { useInvestigation, useInvestigations, useMarkDraftUsed, useStartInvestigation } from '@/queries/investigations'
import { isStale } from '@/types/investigation'
import { EvidenceMap } from './EvidenceMap'
import { StepsInspector } from './StepsInspector'
import { DebugTab } from './DebugTab'
import { InvestigationPanel } from './InvestigationPanel'
import { ProposedActions } from './ProposedActions'
import { handOffDraft } from './draftHandoff'
import { sourceOf } from './evidence'
import { triageDepthLabel } from './triageLabels'
import { SummaryText } from './SummaryText'
import { LearningEditor } from '@/features/learnings/LearningEditor'
import { learningFromInvestigation } from '@/features/learnings/fromInvestigation'
import type { InvestigationWithTriage } from '@/queries/investigations'
import type { ProposalStatus } from '@/types/investigation'

const DECIDED: ProposalStatus[] = ['applied', 'dismissed']

function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'success' | 'warning' | 'danger' }) {
  const tint = {
    neutral: 'bg-etn-control-subtle text-etn-text-label',
    success: 'bg-etn-success-subtle text-etn-success-text',
    warning: 'bg-etn-warning-subtle text-etn-warning-text',
    danger: 'bg-etn-danger-subtle text-etn-danger-text'
  }[tone]
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-etn-pill px-2.5 py-0.5 text-etn-micro font-semibold ${tint}`}>
      {children}
    </span>
  )
}

function duration(from?: string | null, to?: string | null): string | null {
  if (!from || !to) return null
  const seconds = Math.round((Date.parse(to) - Date.parse(from)) / 1000)
  if (!Number.isFinite(seconds) || seconds < 0) return null
  return seconds >= 60 ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : `${seconds}s`
}

function tokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
}

const STATUS_TONE = { completed: 'success', failed: 'danger', running: 'neutral', queued: 'neutral' } as const

/**
 * The full-width view of one Support Agent run: its conclusion and the evidence map (what it read,
 * found and proposes), with the step inspector and the debug payload one tab away.
 */
export function InvestigationPage({ ticketId, runId }: { ticketId: string; runId?: string }) {
  const navigate = useNavigate()
  const { data: ticketData } = useTicket(ticketId)
  const { data: list } = useInvestigations(ticketId)
  const runs = useMemo(() => [...(list?.data ?? [])].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)), [list])
  const run = (runId ? runs.find(r => r._id === runId) : runs[0]) as InvestigationWithTriage | undefined
  const live = Boolean(run && (run.status === 'queued' || run.status === 'running') && !isStale(run))
  const { data: detail } = useInvestigation(ticketId, run?._id ?? '', Boolean(run), live)
  const start = useStartInvestigation(ticketId)
  const markDraftUsed = useMarkDraftUsed(ticketId, run?._id ?? '')
  const [savingLearning, setSavingLearning] = useState(false)
  const [savedLearning, setSavedLearning] = useState<{ _id: string; title: string } | null>(null)

  if (!run) return <p className="p-6 text-sm text-etn-text-soft">This ticket has no Support Agent runs yet.</p>

  const result = run.result
  const steps = detail?.debug?.steps ?? []
  const usage = detail?.debug?.usage
  const reads = steps.filter(s => !s.isError)
  const sources = new Set(reads.map(s => sourceOf(s.tool)))
  const ticket = ticketData?.ticket
  const reporterName = ticket?.creator?.name
  const earlier = runs.filter(r => r._id !== run._id)

  const insertDraft = () => {
    if (!result?.draftReply) return
    markDraftUsed.mutate()
    handOffDraft(ticketId, result.draftReply)
    navigate({ to: '/admin/tickets/$id', params: { id: ticketId } })
  }

  return (
    <div className="mx-auto max-w-[1120px] pb-12" data-testid="investigation-page">
      <div className="mb-1.5 text-xs text-etn-text-soft">
        <Link to="/admin/tickets/$id" params={{ id: ticketId }} className="text-etn-accent-text-active hover:underline">
          {ticket ? `${formatTicketRef(ticket)} ${ticket.subject}` : 'Back to the ticket'}
        </Link>{' '}
        / Investigation
      </div>

      <header className="flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
        <div className="min-w-0">
          <h2 className="text-[1.25rem] font-semibold leading-tight">{result?.title || result?.summary || 'Support Agent investigation'}</h2>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="run-meta">
            <Pill tone={STATUS_TONE[run.status]}>
              {run.status === 'completed' ? 'Completed' : run.status === 'failed' ? 'Failed' : 'In progress'}
            </Pill>
            {run.triage && <Pill>{triageDepthLabel(run.triage.depth).replace(/^./, c => c.toUpperCase())}</Pill>}
            {run.model?.id && <Pill>{run.model.id}</Pill>}
            <Pill>
              Ran {formatDateTime(run.startedAt ?? run.createdAt)}
              {duration(run.startedAt, run.completedAt) ? `, ${duration(run.startedAt, run.completedAt)}` : ''}
            </Pill>
            {run.requestedByName && <Pill>Requested by {run.requestedByName}</Pill>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? <Loader2 className="animate-spin" /> : <RotateCw />}
            Run again
          </Button>
          {run.status === 'completed' && result && (
            // Waits for the steps: the editor prefills its confirmation checks from them once
            <Button size="sm" variant="outline" disabled={!detail} onClick={() => setSavingLearning(open => !open)}>
              <Lightbulb />
              Save as learning
            </Button>
          )}
          {result?.draftReply && (
            <Button size="sm" onClick={insertDraft}>
              Use draft reply
            </Button>
          )}
        </div>
      </header>

      {result && (
        <div className="mt-4 grid grid-cols-1 gap-5 rounded-etn-card bg-etn-surface-sunken px-4.5 py-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <div>
            <p className="mb-2.5 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">What it concluded</p>
            <SummaryText text={result.summary} className="max-w-[70ch] gap-2.5 text-sm text-etn-text-cell" />
          </div>
          <div>
            <div className="text-xs text-etn-text-label" data-testid="confidence">
              Confidence
              <strong className="block text-xl tabular-nums text-etn-text">{result.confidence}%</strong>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-etn-border-hairline">
                <i className="block h-full bg-etn-accent" style={{ width: `${result.confidence}%` }} />
              </div>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs tabular-nums text-etn-text-soft" data-testid="run-stats">
              <span>
                Reads <b className="font-semibold text-etn-text-cell">{reads.length}</b>
              </span>
              <span>
                Sources <b className="font-semibold text-etn-text-cell">{sources.size}</b>
              </span>
              <span>
                Tokens <b className="font-semibold text-etn-text-cell">{usage ? tokens(usage.inputTokens + usage.outputTokens) : 'n/a'}</b>
              </span>
              <span>
                Cost <b className="font-semibold text-etn-text-cell">{usage?.estimatedCost != null ? `$${usage.estimatedCost.toFixed(2)}` : 'n/a'}</b>
              </span>
            </div>
          </div>
        </div>
      )}

      {savedLearning && (
        <p className="mt-4 text-etn-control text-etn-success-text" role="status">
          Saved the learning{' '}
          <Link to="/admin/learnings" search={{ id: savedLearning._id }} className="font-semibold hover:underline">
            {savedLearning.title}
          </Link>
        </p>
      )}

      {savingLearning && result && (
        <section className="mt-4 rounded-etn-card border border-etn-border-hairline bg-etn-surface p-4" data-testid="save-learning">
          <h3 className="mb-1 text-sm font-semibold">Save as learning</h3>
          <p className="mb-3 text-xs text-etn-text-soft">
            Prefilled from this run. Edit it before saving: the Support Agent will act on it for future tickets.
          </p>
          <LearningEditor
            initial={learningFromInvestigation({ result, steps, proposals: run.proposedActions ?? [], companyId: ticket?.companyId ?? null })}
            sourceInvestigationId={run._id}
            onCancel={() => setSavingLearning(false)}
            onSaved={saved => {
              setSavingLearning(false)
              setSavedLearning({ _id: saved._id, title: saved.title })
            }}
          />
        </section>
      )}

      {run.status === 'completed' && result ? (
        <Tabs defaultValue="map" className="mt-5">
          <TabsList variant="line">
            <TabsTrigger value="map">Evidence map</TabsTrigger>
            <TabsTrigger value="steps">Steps</TabsTrigger>
            <TabsTrigger value="debug">Debug</TabsTrigger>
          </TabsList>
          <TabsContent value="map" className="pt-4">
            {result.matchedLearning && (
              <div
                className="mb-4 flex items-center gap-2 rounded-etn-card bg-etn-accent-subtle px-3.5 py-2.5 text-etn-control text-etn-accent-text-active"
                data-testid="matched-learning"
              >
                <Lightbulb className="h-4 w-4" />
                Matched learning:{' '}
                <Link to="/admin/learnings" search={{ id: result.matchedLearning.id }} className="font-semibold hover:underline">
                  {result.matchedLearning.title}
                </Link>
              </div>
            )}
            <EvidenceMap
              ticketId={ticketId}
              invId={run._id}
              result={result}
              steps={steps}
              proposals={run.proposedActions ?? []}
              reporterName={reporterName}
              onUseDraft={insertDraft}
            />
          </TabsContent>
          <TabsContent value="steps" className="pt-4">
            <StepsInspector steps={steps} result={result} learningMatch={run.learningMatch} />
          </TabsContent>
          <TabsContent value="debug" className="pt-4">
            <DebugTab ticketId={ticketId} invId={run._id} />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="mt-5">
          <InvestigationPanel
            ticketId={ticketId}
            onUseAsReply={text => {
              handOffDraft(ticketId, text)
              navigate({ to: '/admin/tickets/$id', params: { id: ticketId } })
            }}
          />
        </div>
      )}

      {earlier.map(r => {
        const open = (r.proposedActions ?? []).filter(p => !DECIDED.includes(p.status))
        return open.length ? (
          <div key={r._id} className="mt-6">
            <ProposedActions
              ticketId={ticketId}
              invId={r._id}
              proposals={open}
              title={`Proposed changes from the run on ${formatDateTime(r.createdAt)}`}
            />
          </div>
        ) : null
      })}
    </div>
  )
}
